"use client";

/**
 * Page bar — the one strip of "where am I / which record is this" chrome that
 * every authenticated page shares.
 *
 *     ‹  Projects › Symfony Demo                        [‹ 1 of 6 ›]  [⋯]
 *
 * Three things used to be hand-rolled per page and drifted apart:
 *
 *   1. **Alignment.** `PageHeader` laid the breadcrumb row out with
 *      `items-start`, so the 26px pager box and the 18px crumb text sat on
 *      different baselines — visible in every screenshot as "1 of 6" hanging
 *      half a line below the trail. Four other detail pages (agents,
 *      workspaces, patches, issues) each built their own version of the same
 *      row, with their own gap, their own alignment and their own decision
 *      about whether the pager belonged next to the crumbs or in among the
 *      action buttons.
 *   2. **Persistence.** The row scrolled away. On a long project page the
 *      operator lost both the way back up the trail AND the record stepper
 *      as soon as they read past the first card.
 *   3. **Delete.** Deleting the record you have open was a per-page
 *      invention — present on projects (buried in a use-case-resolved action
 *      menu), absent everywhere else.
 *
 * This component owns all three. It sticks to the top of the scroll
 * container, it centres its two clusters on one line, and it carries the
 * record-level overflow menu with a standard, confirmed delete.
 *
 * ## Sticky offsets
 *
 * The bar sticks below whatever chrome is already pinned above it
 * (`--settings-nav-h`, published by the settings layout's mobile disclosure
 * bar) and publishes its OWN height as `--page-bar-h` so the sticky chrome
 * *below* it — a `<DataTable>` toolbar, `<OperatorPage.Toolbar>` — can offset
 * itself instead of piling up on `top: 0`. The variable lives on
 * `documentElement` because the consumers are not descendants of this bar;
 * it is set on mount and removed on unmount, and React runs the outgoing
 * page's cleanup before the incoming page's effect, so a route change never
 * leaves a stale offset behind.
 */

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Trash2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n/provider";
import { Breadcrumbs, useBreadcrumbTrail, type BreadcrumbCrumb } from "@/components/breadcrumbs";
import { ActionMenu, type ActionMenuItem } from "@/components/ui/action-menu";
import { ConfirmAction } from "@/components/ui/confirm-action";
import { useToast } from "@/components/ui/toast";

/** Bar height, in px. Fixed rather than measured: the bar has exactly one
 *  row of 12.5px text and 28px controls, so measuring it would only add a
 *  layout pass and a first-paint jump for consumers reading `--page-bar-h`. */
const BAR_HEIGHT = 44;

/**
 * The record-level delete every detail page gets for free.
 *
 * The bar owns the whole flow — confirm dialog, the DELETE call, the failure
 * toast, and the redirect back to the list — because every page that rolled
 * its own got a slightly different subset of it (one forgot the confirm, two
 * forgot to surface a server refusal).
 */
export type PageBarRemove = {
  /** DELETE target, query string included (e.g. `?tenant=…`). */
  endpoint: string;
  /** Where to land once the record is gone — normally its list page. */
  redirectTo: string;
  /** The record's name, interpolated into the confirmation copy. */
  entity: string;
  /** Menu label. Defaults to the generic "Delete <thing>". */
  label?: string;
  /** Confirmation overrides, when the generic copy is too vague. */
  title?: string;
  body?: string;
  /**
   * Per-entity impact line for the confirmation, appended after the generic
   * "this removes X from active work" sentence.
   *
   * The generic copy used to read "This permanently deletes X. This cannot be
   * undone." — which is now false for every entity this bar deletes: issues,
   * workspaces, projects and codebases are soft-deleted (hidden, restorable,
   * audited) and their governance history is retained. The three-line shape is
   * deliberate: what goes / what stays / whether it comes back. Pass `impact`
   * to name the entity's own "what stays / comes back"; omit it and the
   * conservative default (`confirmKept` + `confirmReversible`) is used.
   */
  impact?: string;
  /**
   * Run instead of the built-in DELETE. For pages whose removal is not a
   * plain endpoint call (a multi-step archive, a store mutation). Returning
   * `false` suppresses the redirect — the handler reported its own failure.
   */
  onRemove?: () => Promise<boolean | void> | boolean | void;
};

export type PageBarProps = {
  /**
   * Explicit trail (detail pages own their return-aware chain). Omit to
   * derive it from the route, with `leaf` as the current page.
   */
  breadcrumb?: BreadcrumbCrumb[];
  /** Current-page label in derived mode — normally the page title. */
  leaf?: string;
  /**
   * Fallback eyebrow for routes with no resolvable trail. Rendered as the
   * quiet uppercase line `<Breadcrumbs>` has always used, WITHOUT the sticky
   * strip: a lone section label is not worth pinning a bar for.
   */
  eyebrow?: string;
  /**
   * Navigation chrome for the right cluster — in practice `<DetailPager>`.
   * It answers "where am I in the list", which is the same question as the
   * breadcrumbs, so it belongs on this row and not among the page's action
   * buttons, where it competed with the primary button for attention.
   */
  aside?: React.ReactNode;
  /** Record-level overflow items, rendered before the standard delete. */
  menu?: ActionMenuItem[];
  /** Standard delete. Omit on pages where the record cannot be removed. */
  remove?: PageBarRemove;
  className?: string;
};

/**
 * The standard delete as a menu item plus its confirmation dialog.
 *
 * Exported separately from `<PageBar>` for the surfaces whose header is NOT
 * a page bar — the workspace cockpit keeps its trail in a fixed pane header,
 * where a sticky, gutter-bleeding strip would be wrong — so that "delete the
 * record you have open" is still ONE implementation of confirm → call →
 * report the refusal → return to the list, and not a second, subtly
 * different copy of it.
 *
 * Pass `undefined` when the operator may not delete this record (or the
 * record cannot be deleted at all): the item is then `null` and the caller
 * renders no affordance.
 */
export function useRecordRemoval(remove?: PageBarRemove): {
  item: ActionMenuItem | null;
  dialog: React.ReactNode;
} {
  const t = useT();
  const router = useRouter();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const runRemove = useCallback(async () => {
    if (!remove) return;
    setBusy(true);
    try {
      if (remove.onRemove) {
        const ok = await remove.onRemove();
        if (ok === false) return;
      } else {
        const res = await fetch(remove.endpoint, { method: "DELETE" });
        if (!res.ok) {
          const body = await res.json().catch(() => ({} as { error?: string }));
          toast.err(body?.error ?? t("pageBar.remove.error", { status: String(res.status) }));
          return;
        }
      }
      setConfirmOpen(false);
      router.push(remove.redirectTo);
      router.refresh();
    } catch (e) {
      toast.err((e as Error)?.message ?? t("pageBar.remove.error", { status: "network" }));
    } finally {
      setBusy(false);
    }
  }, [remove, router, toast, t]);

  if (!remove) return { item: null, dialog: null };

  return {
    item: {
      id: "record.remove",
      label: remove.label ?? t("pageBar.remove.label"),
      icon: Trash2,
      destructive: true,
      onSelect: () => setConfirmOpen(true),
    },
    dialog: (
      <ConfirmAction
        action="delete"
        open={confirmOpen}
        onClose={() => { if (!busy) setConfirmOpen(false); }}
        onConfirm={runRemove}
        entity={remove.entity}
        title={remove.title ?? t("pageBar.remove.confirmTitle", { name: remove.entity })}
        body={
          remove.body ?? (
            <span className="space-y-1.5 block">
              <span className="block">{t("pageBar.remove.confirmBody", { name: remove.entity })}</span>
              <span className="block">
                {remove.impact ?? `${t("pageBar.remove.confirmKept")} ${t("pageBar.remove.confirmReversible")}`}
              </span>
            </span>
          )
        }
        confirmLabel={t("pageBar.remove.confirm")}
        cancelLabel={t("common.cancel")}
      />
    ),
  };
}

export function PageBar({
  breadcrumb, leaf, eyebrow, aside, menu, remove, className,
}: PageBarProps) {
  const t = useT();
  const crumbs = useBreadcrumbTrail({ items: breadcrumb, leaf });
  const removal = useRecordRemoval(remove);

  const hasBar = crumbs.length > 0;

  useEffect(() => {
    if (!hasBar) return;
    const root = document.documentElement;
    root.style.setProperty("--page-bar-h", `${BAR_HEIGHT}px`);
    return () => { root.style.removeProperty("--page-bar-h"); };
  }, [hasBar]);

  // No trail at all (an unmapped route with only an eyebrow): keep the old
  // quiet line rather than pinning an almost-empty strip to the viewport.
  if (!hasBar) return <Breadcrumbs leaf={leaf} eyebrow={eyebrow} />;

  const items: ActionMenuItem[] = [...(menu ?? []), ...(removal.item ? [removal.item] : [])];

  return (
    <>
      <div
        data-testid="page-bar"
        style={{ height: BAR_HEIGHT }}
        className={cn(
          // Sticks under the settings layout's mobile disclosure bar where
          // there is one; flush to the top of <main> everywhere else.
          "sticky top-[var(--settings-nav-h,0px)] z-30",
          // Bleed to the page gutter so the strip reads as full-width chrome
          // rather than a floating card. The values match the canonical page
          // padding (`p-4 sm:p-6`); the handful of `p-8` pages simply keep a
          // slightly wider gutter, which costs nothing because a page's own
          // content never renders inside its padding.
          "-mx-4 sm:-mx-6 px-4 sm:px-6",
          "flex items-center justify-between gap-3",
          // Fully opaque, not the translucent `sticky-chrome` treatment used
          // by taller sticky regions: at 44px the page content scrolling
          // underneath was still legible THROUGH the bar, and a trail you
          // have to read past a ghosted page title is worse than no trail.
          "border-b border-line-subtle bg-bg-base",
          className,
        )}
      >
        <Breadcrumbs items={crumbs} divider={false} className="min-w-0 flex-1" />
        {(aside || items.length > 0) && (
          <div className="flex shrink-0 items-center gap-2">
            {aside}
            {items.length > 0 && (
              <ActionMenu
                items={items}
                label={t("pageBar.more")}
                data-testid="page-bar-menu"
              />
            )}
          </div>
        )}
      </div>

      {removal.dialog}
    </>
  );
}
